import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
} from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { Observable, of } from 'rxjs';
import { EditMode, FarmResource } from 'src/app/flock/models/flock.model';

export interface ResourceEditDialogInputData extends AbstractDialogData {
  resourceRow?: FarmResource;
  mode: EditMode;
}

export interface ResourceEditDialogResultData {
  resourceRow: FarmResource;
}

@Component({
  selector: 'app-resource-edit.component',
  templateUrl: 'resource-edit.component.html',
  standalone: false,
})
export class ResourceEditComponent
  extends AbstractDialog<ResourceEditDialogResultData>
  implements OnInit
{
  formGroup = new FormGroup({
    year: new FormControl<number>(null, [Validators.required]),
    electricity: new FormControl<number>(null, [Validators.min(0)]),
    electricityPhotovoltaic: new FormControl<number>(null, [Validators.min(0)]),
    electricityWind: new FormControl<number>(null, [Validators.min(0)]),
    naturalGas: new FormControl<number>(null, [Validators.min(0)]),
    diesel: new FormControl<number>(null, [Validators.min(0)]),
    gasoline: new FormControl<number>(null, [Validators.min(0)]),
    coal: new FormControl<number>(null, [Validators.min(0)]),
    propaneLpg: new FormControl<number>(null, [Validators.min(0)]),
    water: new FormControl<number>(null, [Validators.min(0)]),
  });

  years$: Observable<number[]>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ResourceEditDialogInputData,
    protected mdDialogRef: MatDialogRef<ResourceEditComponent, AbstractDialogResultData>,
    private readonly localeService: LocaleService,
  ) {
    super();
    const yearsTo = new Date().getFullYear();
    let yearsFrom = yearsTo - 5;
    if (this.data.resourceRow && this.data.resourceRow.year < yearsFrom) {
      yearsFrom = this.data.resourceRow.year;
    }

    this.years$ = of(Array.from({ length: yearsTo - yearsFrom }, (_, i) => yearsTo - i));
    this.data.title = this.localeService.translate(this.data.mode);
  }

  ngOnInit() {
    if (this.data.mode === EditMode.Edit && this.data.resourceRow !== null) {
      this.formGroup.controls.year.setValue(this.data.resourceRow.year);
      this.formGroup.controls.electricity.setValue(this.data.resourceRow.electricity);
      this.formGroup.controls.electricityPhotovoltaic.setValue(
        this.data.resourceRow.electricityPhotovoltaic,
      );
      this.formGroup.controls.electricityWind.setValue(this.data.resourceRow.electricityWind);
      this.formGroup.controls.naturalGas.setValue(this.data.resourceRow.naturalGas);
      this.formGroup.controls.diesel.setValue(this.data.resourceRow.diesel);
      this.formGroup.controls.gasoline.setValue(this.data.resourceRow.gasoline);
      this.formGroup.controls.coal.setValue(this.data.resourceRow.coal);
      this.formGroup.controls.propaneLpg.setValue(this.data.resourceRow.propaneLpg);
      this.formGroup.controls.water.setValue(this.data.resourceRow.water);
    }
  }

  save() {
    this.saveResource();
  }

  private saveResource() {
    if (this.formGroup.valid) {
      super.confirm({
        resourceRow: {
          id: this.data.resourceRow?.id,
          year: this.formGroup.controls.year.value,
          electricity: this.formGroup.controls.electricity.value,
          electricityPhotovoltaic: this.formGroup.controls.electricityPhotovoltaic.value,
          electricityWind: this.formGroup.controls.electricityWind.value,
          naturalGas: this.formGroup.controls.naturalGas.value,
          diesel: this.formGroup.controls.diesel.value,
          gasoline: this.formGroup.controls.gasoline.value,
          coal: this.formGroup.controls.coal.value,
          propaneLpg: this.formGroup.controls.propaneLpg.value,
          water: this.formGroup.controls.water.value,
        },
      });
    }
  }
}
